<template>
  <div
    :class="[
      $style.layout,
      {
        [$style['layout--standalone']]: standalone,
        [$style['layout--personal']]: personal,
      },
    ]"
    @click="
      !standalone &&
        injectedFetchPost &&
        injectedFetchPost(participation.sc_team?.slug)
    "
  >
    <div :class="$style.result">
      <img
        v-if="!!rewardIcon"
        :src="`/img/${rewardIcon}.svg`"
        alt=""
        :class="[$style.reward, { [$style['reward--top']]: isTop }]"
      />
      <div
        :class="[
          $style.rank,
          !calculatedRank
            ? $style['rank--empty']
            : {
                [$style['rank--top']]: isTop,
                [$style['rank--gold']]: isGold,
                [$style['rank--silver']]: isSilver,
                [$style['rank--bronze']]: isBronze,
                [$style['rank--rewarded']]: isRewarded,
                [$style['rank--personal']]: personal,
              },
        ]"
      >
        #{{ calculatedRank }}
      </div>
      <div :class="$style.record">
        <div>
          <template v-if="isScoreContest">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 17.332 16.094"
            >
              <path
                fill="currentColor"
                d="M16.674 5.571h-5.691L9.25.41a.627.627 0 0 0-1.176 0L6.35 5.571H.619A.621.621 0 0 0 0 6.19a.455.455 0 0 0 .012.1.6.6 0 0 0 .259.437l4.679 3.298-1.8 5.222a.621.621 0 0 0 .213.7.6.6 0 0 0 .348.151.758.758 0 0 0 .387-.139l4.565-3.254 4.565 3.254a.725.725 0 0 0 .387.139.556.556 0 0 0 .344-.151.613.613 0 0 0 .213-.7l-1.792-5.222 4.639-3.327.112-.1a.592.592 0 0 0-.457-1.033Z"
              />
            </svg>
          </template>
          <template v-else-if="isRunContest">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 15.857 15.861"
            >
              <g fill="currentColor">
                <path
                  d="M7.93 15.862a7.931 7.931 0 0 1-5.5-13.641.637.637 0 1 1 .884.916 6.656 6.656 0 1 0 5.248-1.833v2.482a.638.638 0 0 1-1.277 0V.638A.638.638 0 0 1 7.923 0a7.931 7.931 0 0 1 0 15.861Z"
                />
                <path
                  d="m4.754 4.199 3.945 2.835a1.194 1.194 0 0 1-1.387 1.939 1.153 1.153 0 0 1-.278-.278L4.199 4.75a.4.4 0 0 1 .555-.555Z"
                />
              </g>
            </svg>
          </template>
          {{ activityWording }}
        </div>
        <span>{{ formattedRecord }}</span>
      </div>
    </div>
    <div :class="$style.posts">
      <div
        v-for="(record, i) in participation.sc_records"
        :key="i"
        :class="[$style.post, { [$style['post--solo']]: isSoloContest }]"
      >
        <div :class="$style.post__content">
          <div :class="$style.post__media">
            <NuxtImg
              v-if="isImageContest"
              :src="record.image?.url"
              loading="lazy"
              alt=""
              width="640"
              quality="80"
              format="auto"
            />
            <template v-else-if="isVideoContest">
              <lite-youtube
                v-if="videoInfo(record.video).isYoutube"
                class="iframe-placeholder"
                :videoid="videoInfo(record.video).cover"
                playlabel="Afficher la vidéo"
              />
              <iframe
                v-else
                :src="videoInfo(record.video).cover"
                frameborder="0"
                class="p-video__iframe"
                loading="lazy"
              />
              <div :class="$style.layer" />
            </template>
          </div>
        </div>
        <div :class="$style.infos">
          <div :class="$style.infos__waper">
            <NuxtImg
              :src="record.user?.avatar?.url"
              :class="$style.infos__avatar"
              loading="lazy"
              quality="80"
              width="42"
              height="42"
              sizes="sm:42px tb:84px"
              fit="cover"
              format="auto"
            />
            <p :class="$style.infos__nickname">
              {{ record.user?.nickname }}
            </p>
          </div>
          <div :class="$style.infos__record">
            {{ formatPostRecord(isScoreContest ? record.score : record.run) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CustomSpeedContest } from '~/types'
import {
  activityWordingKey,
  injectedFetchPostKey,
  isImageContestKey,
  isLockedRankingKey,
  isRunContestKey,
  isRunSecondsContestKey,
  isScoreContestKey,
  isSoloContestKey,
  isVideoContestKey,
} from '~/utils/provideKeys'

const isVideoContest = inject(isVideoContestKey)

onMounted(async () => {
  await nextTick()
  if (!isVideoContest?.value) return
  import('@justinribeiro/lite-youtube')
})

const props = withDefaults(
  defineProps<{
    rank?: number
    standalone?: boolean
    personal?: boolean
    participation: NonNullable<CustomSpeedContest['invitations']>[number]
  }>(),
  {
    rank: 0,
    standalone: false,
    personal: false,
  },
)

const activityWording = inject(activityWordingKey)
const injectedFetchPost = inject(injectedFetchPostKey)
const isImageContest = inject(isImageContestKey)
const isScoreContest = inject(isScoreContestKey)
const isRunContest = inject(isRunContestKey)
const isRunSecondsContest = inject(isRunSecondsContestKey)
const isSoloContest = inject(isSoloContestKey)
const isLockedRanking = inject(isLockedRankingKey)

const calculatedRank = computed(() => {
  return props.participation.position || props.rank
})

const recordContestsStore = useRecordContestsStore()
const { recordContest } = storeToRefs(recordContestsStore)

const lastPlaceRewarded = computed(() => {
  return recordContest.value?.winner_number
})

const isFromRanking = computed(() => {
  return !props.personal && !props.standalone
})
const isTop = computed(() => {
  return props.rank <= 3 && isFromRanking.value
})

const isGold = computed(() => {
  if (lastPlaceRewarded.value) {
    return (
      isLockedRanking?.value &&
      isFromRanking.value &&
      props.rank === 1 &&
      props.rank <= lastPlaceRewarded.value
    )
  }
  return false
})

const isSilver = computed(() => {
  if (lastPlaceRewarded.value) {
    return (
      isLockedRanking?.value &&
      isFromRanking.value &&
      props.rank === 2 &&
      props.rank <= lastPlaceRewarded.value
    )
  }
  return false
})

const isBronze = computed(() => {
  if (lastPlaceRewarded.value) {
    return (
      isLockedRanking?.value &&
      isFromRanking.value &&
      props.rank === 3 &&
      props.rank <= lastPlaceRewarded.value
    )
  }
  return false
})

const isRewarded = computed(() => {
  if (lastPlaceRewarded.value) {
    return (
      isLockedRanking?.value &&
      isFromRanking.value &&
      (isTop.value || props.rank <= lastPlaceRewarded.value)
    )
  }
  return false
})

const rewardIcon = computed(() => {
  if (!isRewarded.value) return ''
  if (isGold.value) return 'cup-gold'
  if (isSilver.value) return 'cup-silver'
  if (isBronze.value) return 'cup-bronze'

  return 'cup-star'
})

const formattedRecord = computed(() => {
  if (isScoreContest?.value) {
    return formatNumber(props.participation.score)
  }
  if (isRunContest?.value) {
    return formatRun(props.participation.run, isRunSecondsContest?.value)
  }

  return ''
})

const videoInfo = (videoUrl?: string) => {
  const defaultValue = {
    isYoutube: false,
    cover: '',
  }

  if (!videoUrl) return defaultValue

  const videoData = extractVideoId(videoUrl)

  if (!videoData) return defaultValue

  const isYoutube = videoData.source === 'youtube'

  return {
    isYoutube,
    cover: isYoutube
      ? videoData.id
      : buildEmbedUrl(videoData.source, videoData.id),
  }
}

const formatPostRecord = (record?: number) => {
  if (isScoreContest?.value) {
    return formatNumber(record)
  }

  if (isRunContest?.value) {
    return formatRun(record, isRunSecondsContest?.value)
  }

  return record
}
</script>

<style lang="scss" module>
.layout {
  $root: &;

  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  cursor: pointer;
  transition: all 0.35s ease-in-out;

  @media (any-hover: hover) {
    &:not(#{$root}--standalone):hover {
      .rank {
        background-position: 100% 0;
      }

      .post {
        &__media {
          transform: scale(1.03);
          transition: all 0.35s ease-in-out;
        }
      }
    }
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: -40px;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: $white;
    pointer-events: none;
  }

  &:not(:first-child) {
    margin-top: 50px;
  }

  &:last-of-type {
    margin-bottom: 90px;

    &::after {
      content: none;
    }
  }

  @media (min-width: 992px) {
    flex-direction: row;
  }

  &--standalone,
  &--personal {
    margin: 30px 0 40px !important;

    @media (min-width: 992px) {
      margin: 50px 0 !important;
    }

    &::after {
      content: none;
    }
  }

  &--standalone {
    cursor: auto;
  }
}

.result {
  position: relative;
  margin-bottom: 15px;

  @media (min-width: 992px) {
    align-self: flex-end;
    margin-bottom: 0;
  }
}

.reward {
  position: absolute;
  top: -10px;
  left: 0;
  z-index: 1;
  width: 60px;
  height: 60px;
  object-fit: contain;
  object-position: top center;

  @media (min-width: 992px) {
    top: -12px;
    width: 90px;
    height: 90px;
  }

  @media (min-width: 1600px) {
    top: -17px;
    width: 125px;
    height: 125px;
  }

  &--top {
    top: -20px;
    left: 15px;
    width: 53px;
    height: 75px;

    @media (min-width: 992px) {
      top: -30px;
      left: 25px;
      width: 80px;
      height: 115px;
    }

    @media (min-width: 1600px) {
      top: -45px;
      left: 30px;
      width: 110px;
      height: 160px;
    }
  }
}

.rank {
  --gradient-start: #0d53c5;
  --gradient-intermediate: #fff;
  --gradient-end: #032874;

  display: flex;
  align-items: center;
  width: calc(100% + 25px); // full + gutter
  height: 66px;
  margin-bottom: 10px;
  padding-left: 20px;
  color: $white;
  font-family: 'SST Condensed';
  font-size: 38px;
  font-weight: 700;
  line-height: 1;
  background-image: linear-gradient(
    to right,
    var(--gradient-start) 0%,
    var(--gradient-end) 45%
  );
  background-size: 200% auto;
  transition: all 0.35s ease-in-out;
  clip-path: polygon(10px 0, 100% 0%, 100% 100%, 0% 100%);

  @media (min-width: 992px) {
    width: 100%;
    min-width: 320px;
    height: 100px;
    margin-bottom: 40px;
    padding-left: 40px;
    font-size: 60px;
    clip-path: polygon(20px 0, 100% 0%, 100% 100%, 0% 100%);
  }

  @media (min-width: 1600px) {
    min-width: 380px;
    height: 140px;
    font-size: 80px;
  }

  &--empty {
    opacity: 0;
  }

  &--top {
    font-size: 56px;

    @media (min-width: 992px) {
      font-size: 90px;
    }

    @media (min-width: 1600px) {
      font-size: 120px;
    }
  }

  &--rewarded {
    padding-left: 60px;

    @media (min-width: 992px) {
      padding-left: 90px;
    }

    @media (min-width: 1600px) {
      padding-left: 140px;
    }
  }

  &--gold,
  &--silver,
  &--bronze,
  &--personal {
    padding-left: 70px;
    background-image: linear-gradient(
      to right,
      var(--gradient-start) 0%,
      var(--gradient-intermediate) 22%,
      var(--gradient-end) 45%
    );

    @media (min-width: 992px) {
      padding-left: 110px;
      background-image: linear-gradient(
        to right,
        var(--gradient-start) 0%,
        var(--gradient-end) 45%
      );
    }

    @media (min-width: 1600px) {
      padding-left: 160px;
    }
  }

  &--gold {
    --gradient-start: #fff2d0;
    --gradient-intermediate: #ff9f00;
    --gradient-end: #bf6e00;

    @media (min-width: 992px) {
      --gradient-end: #ff9f00;
    }
  }

  &--silver {
    --gradient-start: #fafafa;
    --gradient-intermediate: #707d88;
    --gradient-end: #2b383c;

    @media (min-width: 992px) {
      --gradient-end: #707d88;
    }
  }

  &--bronze {
    --gradient-start: #ffddb5;
    --gradient-intermediate: #8b3c00;
    --gradient-end: #3e170b;

    @media (min-width: 992px) {
      --gradient-end: #8b3c00;
    }
  }

  &--personal {
    --gradient-start: rgb(255 255 255 / 0.25);
    --gradient-intermediate: rgb(131 149 187 / 0.1875);
    --gradient-end: rgb(3 40 116 / 0.125);

    padding-left: 20px;

    @media (min-width: 992px) {
      --gradient-start: rgb(255 255 255 / 0.25);
      --gradient-end: rgb(255 255 255 / 0.125);

      padding-left: 40px;
    }
  }
}

.record {
  display: flex;
  align-items: center;
  color: $white;
  font-family: 'SST Condensed';
  font-size: 16px;

  @media (min-width: 992px) {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
    padding-left: 40px;
    font-size: 24px;
  }

  div {
    display: flex;
    align-items: center;
    text-transform: uppercase;
  }

  svg {
    width: 16px;
    height: 16px;
    margin-right: 5px;
    object-fit: contain;

    @media (min-width: 992px) {
      width: 24px;
      height: 24px;
    }
  }

  span {
    margin-left: 10px;
    padding-bottom: 5px;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;

    @media (min-width: 992px) {
      display: block;
      margin-left: 0;
      padding-bottom: 0;
      font-size: 40px;
    }
  }
}

.posts {
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 992px) {
    flex-grow: 1;
  }

  @media (min-width: 1280px) {
    flex-direction: row;
    gap: 20px;
  }
}

.post {
  position: relative;
  flex-grow: 1;
  flex-basis: 50%;
  overflow: hidden;
  border-bottom-right-radius: 30px;
  border-bottom-left-radius: 30px;

  &--solo {
    flex-grow: 0;
    flex-basis: auto;

    @media (min-width: 992px) {
      width: 400px;
    }

    @media (min-width: 1280px) {
      width: 450px;
    }

    @media (min-width: 1400px) {
      width: 500px;
    }
  }

  &__content {
    overflow: hidden;
  }

  &__media {
    position: relative;
    width: 100%;
    padding-top: 56.25%; // 16x9
    overflow: hidden;
    background-color: rgba($black, 0.5);
    transition: all 0.35s ease-in-out;

    img,
    // lite-youtube element
    .iframe-placeholder,
    lite-youtube,
    // Other players element (Dailymotion, Vimeo)
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      max-width: none;
      height: 100%;
      object-fit: contain;
      border: 0;
    }

    .layer {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 2;
      width: 100%;
      height: 100%;
    }
  }
}

.infos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 80px;
  padding: 0 20px;
  color: $white;
  background: rgb(0 0 0 / 0.7);
  transition: all 0.35s ease-in-out;

  &__waper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
  }

  &__avatar {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    margin-right: 15px;
    overflow: hidden;
    border-radius: 50%;
    object-fit: cover;
  }

  &__nickname {
    flex-grow: 1;
    height: 15px;
    font-family: SST;
    font-size: 10px;
    font-weight: 700;
  }

  &__record {
    font-size: 12px;
  }
}
</style>
